import { useCallback } from 'react';
import type { RiskScoreWindow } from '@amzn/gbsa-helios-typescript-client';

import { useExportRiskMetricGroupMutation } from '../api';
import {
  usePresignedCsvExport,
  type UsePresignedCsvExportResult,
} from './usePresignedCsvExport';

export interface UseRiskMetricGroupExportParams {
  /** The scope to export — mirror the drill-down the breakdown page is showing. */
  readonly businessUnitId: string | undefined;
  readonly location: string;
  readonly metricGroupId: string;
  readonly timeWindow: RiskScoreWindow;
}

export type UseRiskMetricGroupExportResult = UsePresignedCsvExportResult;

/**
 * Drill-down CSV export (the breakdown page): generates a presigned S3 link for one metric group —
 * a root KPI and all its sub-metric drivers — at a single station and window, then downloads it. A
 * thin wrapper over {@link usePresignedCsvExport} that owns only the input shape; the
 * generate-then-download behaviour is shared with the list export. `buildInput` returns `null`
 * until the station and metric group are known, so the trigger no-ops on an unscoped page.
 */
export function useRiskMetricGroupExport({
  businessUnitId,
  location,
  metricGroupId,
  timeWindow,
}: UseRiskMetricGroupExportParams): UseRiskMetricGroupExportResult {
  const mutation = useExportRiskMetricGroupMutation();
  const buildInput = useCallback(() => {
    if (!businessUnitId || !location || !metricGroupId) return null;
    return { businessUnitId, location, metricGroupId, timeWindow };
  }, [businessUnitId, location, metricGroupId, timeWindow]);
  return usePresignedCsvExport(mutation, buildInput);
}
